import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, ChevronRight, HelpCircle, Ambulance, ListChecks } from "lucide-react";
import axios from "axios";
import { ossDashboardSections, ossSectionTheme } from "../../config/ossSectionThemes";
import { useOssAuth } from "../../context/OssAuthContext";
import Oss118Dialog from "../../components/oss/Oss118Dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function greeting(nome) {
  const h = new Date().getHours();
  const saluto = h < 12 ? "Buongiorno" : h < 18 ? "Buon pomeriggio" : "Buonasera";
  return `${saluto}, ${nome}`;
}

function TerapieAlertBanner({ token }) {
  const [summary, setSummary] = useState({ items: [], count: 0, count_overdue: 0 });

  const load = useCallback(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/oss/me/terapie-alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) =>
        setSummary({
          items: data.items || [],
          count: data.count || 0,
          count_overdue: data.count_overdue || 0,
        })
      )
      .catch(() => setSummary({ items: [], count: 0, count_overdue: 0 }));
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  if (summary.count === 0) return null;

  const now = new Date();
  const soonCount = summary.items.filter((item) => {
    const d = new Date(item.scheduled_at);
    const diffMin = (d - now) / 60000;
    return diffMin >= 0 && diffMin <= 30;
  }).length;
  const hasOverdue = summary.count_overdue > 0;

  const tone = hasOverdue ? "danger" : "warn";
  return (
    <section
      aria-label="Promemoria terapie di oggi"
      className={`oss-priority oss-priority--${tone} oss-rise flex flex-col sm:flex-row sm:items-center gap-3 p-4`}
    >
      <div className="flex items-center gap-3.5 flex-1 min-w-0">
        <span
          className={`oss-icon-chip ${hasOverdue ? "bg-red-600" : "bg-amber-500"} text-white`}
          aria-hidden
        >
          <Bell className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold oss-text flex items-center gap-2 flex-wrap text-base">
            Terapie di oggi
            <span
              className={`inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full text-white text-sm font-bold ${
                hasOverdue ? "bg-red-600" : "bg-amber-600"
              }`}
            >
              {summary.count}
            </span>
          </p>
          <p className="text-sm oss-muted">Somministrazioni in scadenza o in ritardo (terapie e extra)</p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-sm font-semibold">
            {hasOverdue ? (
              <span className="oss-text-danger">{summary.count_overdue} in ritardo</span>
            ) : null}
            {soonCount > 0 && (
              <span className="oss-text-warn">
                {soonCount} {soonCount === 1 ? "in scadenza" : "in scadenza"} entro 30 min
              </span>
            )}
          </div>
        </div>
      </div>
      <Link
        to="/oss/fascicoli"
        className={`shrink-0 inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-[0.875rem] px-5 min-h-[48px] text-white transition-colors touch-manipulation whitespace-nowrap ${
          hasOverdue ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"
        }`}
      >
        Visualizza terapie
        <ChevronRight className="w-4 h-4" aria-hidden />
      </Link>
    </section>
  );
}

function PromemoriaOggiBanner({ token }) {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/oss/me/promemoria/oggi`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const complete = async (itemId) => {
    try {
      await axios.post(
        `${API_URL}/api/oss/me/promemoria/${itemId}/completa`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      load();
    } catch {
      // The next refresh will show the current state.
    }
  };

  const pending = items.filter((item) => !item.completato);
  if (!pending.length) return null;

  return (
    <section aria-label="Promemoria di oggi" className="oss-priority oss-priority--info oss-rise oss-rise-2 p-4">
      <div className="flex items-center gap-3.5">
        <span className="oss-icon-chip bg-sky-100 text-sky-700" aria-hidden>
          <ListChecks className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold oss-text text-base">Promemoria di oggi</p>
          <p className="text-sm oss-muted">
            {pending.length} {pending.length === 1 ? "attività da svolgere" : "attività da svolgere"}
          </p>
        </div>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {pending.slice(0, 4).map((item) => (
          <div key={item.id} className="flex items-center gap-3 rounded-[0.875rem] px-2 py-1.5" style={{ background: "var(--oss-surface-2)" }}>
            <button
              type="button"
              onClick={() => complete(item.id)}
              className="w-11 h-11 rounded-full border-2 border-sky-400 text-sky-700 bg-white hover:bg-sky-50 flex items-center justify-center shrink-0 touch-manipulation"
              aria-label={`Completa ${item.titolo}`}
            >
              <Check className="w-4 h-4" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold oss-text truncate">{item.titolo}</p>
              <p className="text-xs oss-muted">{[item.ora, item.note].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
        ))}
        {pending.length > 4 && <p className="pt-1 text-xs oss-muted md:col-span-2">e altre {pending.length - 4} attività</p>}
      </div>
    </section>
  );
}

function todayLabel() {
  const s = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function OssDashboard() {
  const { activeOss, token } = useOssAuth();
  const [show118, setShow118] = useState(false);
  const sections = ossDashboardSections.filter((section) => {
    if (section.requiresResponsabileManutenzioni) {
      return Boolean(activeOss?.responsabile && activeOss?.manutenzioni_enabled);
    }
    if (section.requiresResponsabile) {
      return Boolean(activeOss?.responsabile);
    }
    return true;
  });

  return (
    <div className="flex flex-col gap-5">
      {/* Saluto + 118 sempre in evidenza */}
      <div className="grid gap-3 md:grid-cols-[1fr_minmax(260px,320px)] md:items-stretch oss-rise">
        <div className="flex flex-col justify-center py-1">
          <p className="oss-eyebrow">{todayLabel()}</p>
          <h1 className="mt-1 text-2xl sm:text-[1.75rem] font-bold tracking-tight oss-text leading-tight">
            {greeting(activeOss?.nome || "")}
          </h1>
          <p className="mt-1 text-sm oss-muted">
            {activeOss?.responsabile ? "OSS Responsabile" : "Operatore socio-sanitario"}
            {activeOss?.struttura_nome ? ` · ${activeOss.struttura_nome}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShow118(true)}
          className="oss-emergency relative w-full min-h-[76px] px-5 py-3 flex items-center gap-4 overflow-hidden touch-manipulation text-left transition-transform"
        >
          <span className="oss-icon-chip bg-white/15 ring-1 ring-white/25" aria-hidden>
            <Ambulance className="w-6 h-6 text-white" strokeWidth={1.9} />
          </span>
          <span className="min-w-0 leading-none">
            <span className="block text-[1.75rem] font-extrabold tracking-tight">118</span>
            <span className="block text-xs font-bold text-white/90 tracking-[0.14em] mt-1">EMERGENZA</span>
          </span>
          <svg
            aria-hidden="true"
            className="ml-auto h-8 w-[40%] max-w-[120px] shrink opacity-80"
            viewBox="0 0 160 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <polyline
              points="0,20 28,20 38,20 46,6 54,34 62,6 70,20 88,20 108,20 116,20 124,8 132,32 140,8 148,20 160,20"
              stroke="white"
              strokeWidth="2.25"
              strokeLinejoin="round"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
      </div>

      {/* Priorità di oggi: terapie (ritardo/scadenza) e promemoria prima delle sezioni */}
      <div className="flex flex-col gap-3 empty:hidden">
        <TerapieAlertBanner token={token} />
        <PromemoriaOggiBanner token={token} />
      </div>

      <section aria-labelledby="oss-sezioni-title" className="flex flex-col gap-3">
        <h2 id="oss-sezioni-title" className="oss-eyebrow">Sezioni</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sections.map(({ sectionKey, to, title, subtitle }, i) => {
            const t = ossSectionTheme[sectionKey];
            const Icon = t.Icon;
            const cardSubtitle =
              sectionKey === "turni" && activeOss?.responsabile ? "Turni di tutti e generazione" : subtitle;
            return (
              <Link
                key={to}
                to={to}
                className="oss-tile oss-rise group flex items-center gap-3.5 px-4 py-3.5 min-h-[76px] touch-manipulation"
                style={{ animationDelay: `${Math.min(i, 11) * 25}ms` }}
              >
                <span className={`oss-icon-chip ${t.accentSoft}`} aria-hidden>
                  <Icon className={`w-[22px] h-[22px] ${t.iconClass}`} strokeWidth={1.9} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-[0.95rem] oss-text leading-tight truncate">{title}</span>
                  <span className="block mt-1 text-sm oss-muted leading-tight truncate">{cardSubtitle}</span>
                </span>
                <ChevronRight
                  className="w-5 h-5 shrink-0 oss-muted transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            );
          })}
        </div>
      </section>

      <footer className="flex justify-center">
        <a
          href="mailto:assistenza@affidya.it"
          className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm oss-muted hover:underline"
        >
          <HelpCircle className="w-4 h-4" aria-hidden />
          Serve aiuto? Contatta l'assistenza
        </a>
      </footer>

      <Oss118Dialog open={show118} onClose={() => setShow118(false)} />
    </div>
  );
}
